// src/components/features/PathfinderModules/CalendarBox.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaExpandAlt, FaTimes, FaUpload, FaFileCsv, FaCalendarPlus, FaInfoCircle, FaTrash, FaSpinner, FaDownload } from 'react-icons/fa';
import { FaCalendarAlt, FaCheckCircle, FaClipboardList } from 'react-icons/fa'; // added icons
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list'; // add
import { saveCalendarEvents, fetchCalendarEvents, clearCalendarEvents } from '../../../api/calendarEventApi';
import { fetchTrainingPathway } from '../../../api/trainingPathwayApi';
import { getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { listUserProfiles, listACCSDomainProgresses, listIMTDomainProgresses, listCSTDomainProgresses, listNeuroSurgDomainProgresses, listRadiologyDomainProgresses } from '../../../graphql/queries'; // Updated imports
import IMTSubsteps from '../../../data/PathfinderData/IMT/IMT_Max_Substeps.json';
import CSTSubsteps from '../../../data/PathfinderData/CST/CST_Max_Substeps.json';
import ACCSSubsteps from '../../../data/PathfinderData/ACCS/ACCS_Max_Substeps.json';
import NeuroSurgSubsteps from '../../../data/PathfinderData/Neurosurgery/NeuroSurg_Max_Substeps.json';
import RadiologySubsteps from '../../../data/PathfinderData/Radiology/Radiology_Max_Substeps.json';
import { syncTasksToGoogleCalendar } from '../../../services/googleCalendarService';

const TASK_CONFIG_STORAGE_KEY = 'pfcal-task-settings-v2';
const LEGACY_TASK_CONFIG_KEYS = ['pfcal-task-config'];

const CalendarBox = ({
  expandedContainer,
  onExpand,
  onClose,
  isExpandedInCenter,
  trainingPath = 'IMT'
}) => {
  const isExpanded = expandedContainer === 'calendarBox' || isExpandedInCenter;
  const [uploadType, setUploadType] = useState(null); // 'csv' or 'ics'
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState(null);
  const [generatedTasks, setGeneratedTasks] = useState([]);
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);
  const abortGenRef = useRef(null);
  const [domainProgress, setDomainProgress] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [username, setUsername] = useState(null);
  const [apiTrainingPath, setApiTrainingPath] = useState(null);
  const [apiTrainingPathError, setApiTrainingPathError] = useState(null);
  const clientRef = useRef(null);
  const [accsRawScores, setAccsRawScores] = useState([]);          // NEW raw ACCS items
  const [accsMappingLog, setAccsMappingLog] = useState([]);        // NEW mapping diagnostics
  const [scheduleStartHour, setScheduleStartHour] = useState(9);
  const [scheduleDurationMins, setScheduleDurationMins] = useState(60);
  const [scheduleOffsetDays, setScheduleOffsetDays] = useState(1);
  const [showConfig, setShowConfig] = useState(false);
  const [configMeridiem, setConfigMeridiem] = useState('AM');           // NEW
  const [configPreset, setConfigPreset] = useState('custom');           // NEW: 'morning' | 'afternoon' | 'custom'
  const [configPriority, setConfigPriority] = useState('byDomain');     // NEW: 'byDomain' | 'byProgress'
  const [configAvoidDays, setConfigAvoidDays] = useState([]);           
  const [saveFeedback, setSaveFeedback] = useState(null);
  const saveFeedbackTimerRef = useRef(null);
  const [isGoogleSyncing, setIsGoogleSyncing] = useState(false);
  const [googleSyncError, setGoogleSyncError] = useState(null);
  const [googleSyncSuccess, setGoogleSyncSuccess] = useState(null);

  // Load user's calendar events on component mount
  useEffect(() => {
    const loadCalendarEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const events = await fetchCalendarEvents();
        setCalendarEvents(events);
      } catch (err) {
        console.error('Error loading calendar events:', err);
        setError('Failed to load your calendar events. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadCalendarEvents();
  }, []);

  useEffect(() => {
    clientRef.current = generateClient();
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!user) return;
        setUsername(user.username);
        const { data } = await clientRef.current.graphql({
          query: listUserProfiles,
          variables: { filter: { owner: { eq: user.username } }, limit: 1 },
          authMode: 'userPool'
        });
        const profile = data?.listUserProfiles?.items?.[0] || null;
        setUserProfile(profile);
        console.log('[CalendarBox] Loaded user profile trainingPath:', profile?.trainingPath);
      } catch (err) {
        console.error('[CalendarBox] Failed to load user profile', err);
        setProfileError(err.message);
      } finally {
        setProfileLoaded(true);
      }
    })();
  }, []);

  // NEW: fetch pathway via trainingPathwayApi once we know the username
  useEffect(() => {
    if (!username) return;
    (async () => {
      try {
        const fromApi = await fetchTrainingPathway(username);
        console.log('[CalendarBox] fetchTrainingPathway response:', fromApi);
        setApiTrainingPath(fromApi);
        setApiTrainingPathError(null);
      } catch (err) {
        console.error('[CalendarBox] fetchTrainingPathway failed:', err);
        setApiTrainingPathError(err.message);
        setApiTrainingPath(null);
      }
    })();
  }, [username]);

  // RESOLVED active path: API result → profile field → empty (prop is ignored)
  const activeTrainingPath = useMemo(
    () => (apiTrainingPath || userProfile?.trainingPath || '').trim().toUpperCase(),
    [apiTrainingPath, userProfile?.trainingPath]
  );

  const pathSource = useMemo(() => {
    if (apiTrainingPath) return 'API';
    if (userProfile?.trainingPath) return 'Profile';
    return 'None';
  }, [apiTrainingPath, userProfile?.trainingPath]);

  useEffect(() => {
    if (!profileLoaded) return;
    if (!activeTrainingPath) {
      console.warn('[CalendarBox] No trainingPath available; skipping domain refresh.');
    } else {
      console.log('[CalendarBox] Using resolved trainingPath:', activeTrainingPath, 'source:', pathSource);
    }
  }, [profileLoaded, activeTrainingPath, pathSource]);

  const stripStepNumber = useCallback(
    (text = '') => text.replace(/^\d+\.\s*/, ''),
    []
  );

  const buildPrompt = useCallback(
    (progressList = []) => {
      const lines = progressList.map(d => {
        const descriptor = d.nextStep
          ? `NEXT STEP: ${stripStepNumber(d.nextStep)}`
          : 'ALL CORE STEPS COMPLETE – suggest stretch activity.';
        return `${d.domainKey} | ${d.progress}/${d.total} complete | ${descriptor}`;
      }).join('\n') || 'No domain progress available.';
      return `You are a UK medical specialty portfolio assistant supporting a ${activeTrainingPath} trainee. For each domain below, output ONE next SMART micro-task (≤160 chars, active verb, ≤7 days, UK context).\nReturn ONLY JSON array: [{ "domain": "...", "smartTask": "...", "rationale": "..." }]\nDomain snapshots:\n${lines}\nRules: avoid duplication, tailor to the noted step context, or offer an advanced stretch if all steps complete.`;
    },
    [activeTrainingPath, stripStepNumber]
  );

  // NEW: owner-filtered IMT domain fetch
  const fetchOwnerIMTDomainProgress = useCallback(async () => {
    if (!clientRef.current || !username) return [];
    try {
      const { data } = await clientRef.current.graphql({
        query: listIMTDomainProgresses,
        variables: { filter: { owner: { eq: username } }, limit: 100 },
        authMode: 'userPool'
      });
      const items = data?.listIMTDomainProgresses?.items?.filter(Boolean) || [];
      console.log('[CalendarBox] Owner IMT DomainProgress raw:', items);
      return items;
    } catch (e) {
      console.warn('[CalendarBox] IMTDomainProgress owner fetch failed:', e);
      return [];
    }
  }, [username]);

  // NEW: owner-filtered CST domain fetch
  const fetchOwnerCSTDomainProgress = useCallback(async () => {
    if (!clientRef.current || !username) return [];
    try {
      const { data } = await clientRef.current.graphql({
        query: listCSTDomainProgresses,
        variables: { filter: { owner: { eq: username } }, limit: 100 },
        authMode: 'userPool'
      });
      const items = data?.listCSTDomainProgresses?.items?.filter(Boolean) || [];
      console.log('[CalendarBox] Owner CST DomainProgress raw:', items);
      return items;
    } catch (e) {
      console.warn('[CalendarBox] CSTDomainProgress owner fetch failed:', e);
      return [];
    }
  }, [username]);

  // NEW: owner-filtered ACCS domain fetch (higher precedence)
  const fetchOwnerACCSDomainProgress = useCallback(async () => {
    if (!clientRef.current || !username) return [];
    try {
      const { data } = await clientRef.current.graphql({
        query: listACCSDomainProgresses,
        variables: { filter: { owner: { eq: username } }, limit: 100 },
        authMode: 'userPool'
      });
      const items = data?.listACCSDomainProgresses?.items?.filter(Boolean) || [];
      console.log('[CalendarBox] Owner ACCS DomainProgress raw:', items);
      setAccsRawScores(items);
      return items;
    } catch (e) {
      console.warn('[CalendarBox] ACCSDomainProgress owner fetch failed:', e);
      setAccsRawScores([]);
      return [];
    }
  }, [username]);

  // NEW: owner-filtered Neurosurgery domain fetch
  const fetchOwnerNeuroSurgDomainProgress = useCallback(async () => {
    if (!clientRef.current || !username) return [];
    try {
      const { data } = await clientRef.current.graphql({
        query: listNeuroSurgDomainProgresses,
        variables: { filter: { owner: { eq: username } }, limit: 100 },
        authMode: 'userPool'
      });
      const items = data?.listNeuroSurgDomainProgresses?.items?.filter(Boolean) || [];
      console.log('[CalendarBox] Owner NeuroSurg DomainProgress raw:', items);
      return items;
    } catch (e) {
      console.warn('[CalendarBox] NeuroSurgDomainProgress owner fetch failed:', e);
      return [];
    }
  }, [username]);

  // NEW: owner-filtered Radiology domain fetch
  const fetchOwnerRadiologyDomainProgress = useCallback(async () => {
    if (!clientRef.current || !username) return [];
    try {
      const { data } = await clientRef.current.graphql({
        query: listRadiologyDomainProgresses,
        variables: { filter: { owner: { eq: username } }, limit: 100 },
        authMode: 'userPool'
      });
      const items = data?.listRadiologyDomainProgresses?.items?.filter(Boolean) || [];
      console.log('[CalendarBox] Owner Radiology DomainProgress raw:', items);
      return items;
    } catch (e) {
      console.warn('[CalendarBox] RadiologyDomainProgress owner fetch failed:', e);
      return [];
    }
  }, [username]);

  // Helper: normalize domain keys for matching
  const norm = (s='') => s
    .toLowerCase()
    .replace(/[\s\-_/]+/g,'')
    .replace(/[:()]/g,'')
    .trim();

  const refreshDomainProgress = useCallback(async () => {
    if (!activeTrainingPath) {
      setDomainProgress([]);
      return [];
    }
    try {
      let raw = [];
      let dataset;

      if (activeTrainingPath === 'IMT') {
        raw = await fetchOwnerIMTDomainProgress(); // Use Progress, not Score
        dataset = IMTSubsteps;
      } else if (activeTrainingPath === 'CST') {
        raw = await fetchOwnerCSTDomainProgress(); // Use Progress, not Score
        dataset = CSTSubsteps;
      } else if (activeTrainingPath === 'ACCS') {
        raw = await fetchOwnerACCSDomainProgress(); // ONLY ACCSDomainProgress now
        dataset = ACCSSubsteps;
      } else if (activeTrainingPath === 'NEUROSURGERY') {
        raw = await fetchOwnerNeuroSurgDomainProgress();
        dataset = NeuroSurgSubsteps;
      } else if (activeTrainingPath === 'RADIOLOGY') {
        raw = await fetchOwnerRadiologyDomainProgress();
        dataset = RadiologySubsteps;
      } else {
        dataset = {};
      }

      console.log('[CalendarBox] Raw scores after pathway selection:', raw);

      const flattenSteps = (smartSteps = []) =>
        smartSteps.flatMap(phase => phase.SubSteps || []);

      // Build normalized lookup map from backend raw scores
      const backendMap = {};
      raw.forEach(item => {
        const key = item.domainKey || item.domain || '';
        if (!key) return;
        backendMap[norm(key)] = item;
      });

      const mappingDiagnostics = [];
      const enriched = Object.keys(dataset || {}).map(domainKey => {
        const steps = flattenSteps(dataset[domainKey]?.SMARTSteps || []);
        const normalizedKey = norm(domainKey);
        const backendMatch = backendMap[normalizedKey];

        let progressVal = 0;
        if (backendMatch && typeof backendMatch.progress === 'number') {
          progressVal = backendMatch.progress;
        }

        mappingDiagnostics.push({
          domainKey,
          normalizedKey,
          matched: !!backendMatch,
          backendReportedKey: backendMatch?.domainKey || backendMatch?.domain || null,
          backendProgress: backendMatch?.progress ?? null,
          finalProgressUsed: progressVal
        });

        return {
          domainKey,
          progress: progressVal,
          total: steps.length,
          nextStep: steps[progressVal] || null,
          matched: !!backendMatch
        };
      });

      console.table(mappingDiagnostics);
      setAccsMappingLog(mappingDiagnostics);
      setDomainProgress(enriched);
      return enriched;
    } catch (err) {
      console.error('[CalendarBox] refreshDomainProgress failed:', err);
      setDomainProgress([]);
      return [];
    }
  }, [activeTrainingPath, fetchOwnerACCSDomainProgress, fetchOwnerIMTDomainProgress, fetchOwnerCSTDomainProgress, fetchOwnerNeuroSurgDomainProgress, fetchOwnerRadiologyDomainProgress]);

  useEffect(() => {
    if (!isExpanded) return;
    if (!profileLoaded) return;
    refreshDomainProgress();
  }, [isExpanded, profileLoaded, refreshDomainProgress]);

  const handleUploadClick = (type) => {
    setUploadType(type);
    // Reset file input when changing types
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setError(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      const fileContent = await readFileAsText(file);
      setUploadProgress(25);
      
      if (uploadType === 'csv') {
        const parsedEvents = parseCSV(fileContent);
        setUploadProgress(40);
        
        if (parsedEvents.length > 0) {
          // Save to backend
          setUploadProgress(60);
          await saveCalendarEvents(parsedEvents);
          setUploadProgress(80);
          // Refresh the events from backend
          const events = await fetchCalendarEvents();
          setCalendarEvents(events);
          setError(null);
          setUploadProgress(100);
          // Auto-close upload section and reset after a short delay
          setTimeout(() => {
            setUploadType(null);
            setFileName('');
            setIsUploading(false);
            setUploadProgress(0);
          }, 350);
        } else {
          setError('No valid events found in CSV file');
          setIsUploading(false);
          setUploadProgress(0);
        }
      } else if (uploadType === 'ics') {
        const parsedEvents = parseICS(fileContent);
        setUploadProgress(40);
        if (parsedEvents.length > 0) {
          setUploadProgress(60);
          await saveCalendarEvents(parsedEvents);
          setUploadProgress(80);
          const events = await fetchCalendarEvents();
          setCalendarEvents(events);
          setError(null);
          setUploadProgress(100);
          setTimeout(() => {
            setUploadType(null);
            setFileName('');
            setIsUploading(false);
            setUploadProgress(0);
          }, 350);
        } else {
          setError('No valid events found in ICS file');
          setIsUploading(false);
          setUploadProgress(0);
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(`Error uploading file: ${error.message}`);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const handleClearCalendar = async () => {
    if (!window.confirm('Are you sure you want to clear all calendar events? This cannot be undone.')) {
      return;
    }
    
    try {
      setIsClearing(true);
      const success = await clearCalendarEvents();
      if (success) {
        setCalendarEvents([]);
      } else {
        setError('Some events could not be cleared. Please try again.');
        // Refresh to show remaining events
        const events = await fetchCalendarEvents();
        setCalendarEvents(events);
      }
    } catch (error) {
      console.error('Error clearing calendar events:', error);
      setError('Error clearing calendar events');
    } finally {
      setIsClearing(false);
    }
  };

  const parseCSV = (content = '') => {
    if (typeof content !== 'string') return [];
    // Parse educational timetable CSV format
    const lines = content.split('\n');
    const events = [];

    // Skip header line
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Handle quoted values correctly
      const columns = [];
      let insideQuotes = false;
      let currentValue = '';
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          columns.push(currentValue);
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      
      // Add the last value
      columns.push(currentValue);
      
      // Make sure we have enough columns
      if (columns.length < 13) continue;
      
      // Extract relevant columns
      const description = columns[0].replace(/"/g, '');
      const courseCode = columns[1].replace(/"/g, '');
      const startDate = columns[4].replace(/"/g, '');
      const startTime = columns[5].replace(/"/g, '');
      const endDate = columns[7].replace(/"/g, '');
      const endTime = columns[8].replace(/"/g, '');
      const eventType = columns[10].replace(/"/g, '');
      const staff = columns[11].replace(/"/g, '');
      const location = columns[12].replace(/"/g, '');
      
      // Skip rows with invalid dates
      if (!startDate || !startTime) continue;
      
      // Format dates for FullCalendar
      try {
        const start = new Date(`${startDate}T${startTime}`);
        const end = new Date(`${endDate}T${endTime}`);
        
        // Create event object
        events.push({
          title: `${description} (${eventType})`,
          start: start,
          end: end,
          extendedProps: {
            courseCode: courseCode,
            location: location,
            staff: staff,
            eventType: eventType
          },
          // Add colors based on event type
          backgroundColor: getEventColor(eventType),
          borderColor: getEventColor(eventType)
        });
      } catch (e) {
        console.warn('Could not parse date for event:', description);
      }
    }

    return events;
  };
  
  // Function to assign colors based on event type
  const getEventColor = (eventType) => {
    const typeColors = {
      'Lecture': '#3b82f6', // blue
      'Tutorial': '#10b981', // green
      'Practical': '#f59e0b', // amber
      'Med-CSRC': '#8b5cf6', // purple
      'Med-PDG': '#ec4899', // pink
      'Workshop': '#6366f1', // indigo
      'Event': '#f97316', // orange
      'Med-OSCE': '#ef4444', // red
      'Med-PBL': '#06b6d4', // cyan
      'Med-AMK': '#14b8a6', // teal
      'Online': '#6b7280', // gray
    };
    
    // Default color for unknown types
    return typeColors[eventType] || '#64748b';
  };

  const parseICS = (content) => {
    // Simple ICS parser for demo purposes
    const events = [];
    const lines = content.split('\n');
    let currentEvent = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (line === 'BEGIN:VEVENT') {
        currentEvent = {};
      } else if (line === 'END:VEVENT' && currentEvent) {
        // Only add event if it has required properties
        if (currentEvent.start && currentEvent.title) {
          events.push({
            title: currentEvent.title,
            start: currentEvent.start,
            end: currentEvent.end || null,
            backgroundColor: '#3b82f6',
            borderColor: '#3b82f6',
            extendedProps: {
              location: currentEvent.location || '',
              description: currentEvent.description || ''
            }
          });
        }
        currentEvent = null;
      } else if (currentEvent) {
        if (line.startsWith('SUMMARY:')) {
          currentEvent.title = line.substring(8);
        } else if (line.startsWith('DTSTART:')) {
          // Convert to Date object
          const dateStr = line.substring(8);
          try {
            // Handle format: YYYYMMDDTHHMMSSZ
            const year = dateStr.substr(0, 4);
            const month = dateStr.substr(4, 2) - 1; // JS months are 0-indexed
            const day = dateStr.substr(6, 2);
            const hour = dateStr.substr(9, 2);
            const minute = dateStr.substr(11, 2);
            const second = dateStr.substr(13, 2);
            
            currentEvent.start = new Date(Date.UTC(year, month, day, hour, minute, second));
          } catch (e) {
            console.warn('Could not parse ICS date:', dateStr);
          }
        } else if (line.startsWith('DTEND:')) {
          // Convert to Date object
          const dateStr = line.substring(6);
          try {
            const year = dateStr.substr(0, 4);
            const month = dateStr.substr(4, 2) - 1;
            const day = dateStr.substr(6, 2);
            const hour = dateStr.substr(9, 2);
            const minute = dateStr.substr(11, 2);
            const second = dateStr.substr(13, 2);
            
            currentEvent.end = new Date(Date.UTC(year, month, day, hour, minute, second));
          } catch (e) {
            console.warn('Could not parse ICS end date:', dateStr);
          }
        } else if (line.startsWith('LOCATION:')) {
          currentEvent.location = line.substring(9);
        } else if (line.startsWith('DESCRIPTION:')) {
          currentEvent.description = line.substring(12);
        }
      }
    }

    return events;
  };

  const renderEventContent = (eventInfo) => {
    const title = eventInfo.event.title;
    const descRaw = eventInfo.event.extendedProps?.description || '';
    const desc = descRaw.length > 90 ? descRaw.slice(0, 87) + '…' : descRaw;
    return (
      <div className="pfcalx-taskEvent">
        <div className="pfcalx-taskTitle">{title}</div>
        {desc && (
          <div className="pfcalx-taskDesc">{desc}</div>
        )}
        {eventInfo.event.extendedProps?.location && eventInfo.view.type === 'dayGridDay' && (
          <div className="pfcalx-taskMeta">
            <span className="pfcalx-taskMetaItem">{eventInfo.event.extendedProps.location}</span>
            {eventInfo.event.extendedProps.staff && (
              <span className="pfcalx-taskMetaItem">{eventInfo.event.extendedProps.staff}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompactEventContent = (arg) => {
    const title = arg.event.title || '';
    const descRaw = arg.event.extendedProps?.description || '';
    return (
      <div className="pfcalc-listEvent">
        <div className="pfcalc-listTitle">{title}</div>
        {descRaw && <div className="pfcalc-listDesc">{descRaw}</div>}
        {arg.event.extendedProps?.location && (
          <div className="pfcalc-listMeta">
            <span className="pfcalc-listMetaItem">{arg.event.extendedProps.location}</span>
            {arg.event.extendedProps.staff && (
              <span className="pfcalc-listMetaItem">{arg.event.extendedProps.staff}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompactView = () => (
    <div className="pfcal-compact">
      {/* NEW compact top bar with download + expand */}
      <div className="pfcalc-topbar">
        <button
          className="pfcalc-downloadBtn"
          onClick={generateICSFromTasks}
          disabled={genLoading}
          title="Download tasks as a .ics calendar file"
        >
          {genLoading ? (
            <>
              <FaSpinner className="pfcalc-spin" />
              <span>Preparing…</span>
            </>
          ) : (
            <>
              <FaDownload className="pfcalc-downloadIcon" />
              <span>Download Tasks to Calendar</span>
            </>
          )}
        </button>
        {!isExpanded && !isExpandedInCenter && (
          <button
            className="pfcalc-expandBtn"
            onClick={() => onExpand('calendarBox')}
            title="Expand Calendar Module"
            aria-label="Expand calendar"
          >
            <FaExpandAlt />
          </button>
        )}
      </div>
      <h4 className="pfcal-title">Your Calendar</h4>
      
      {loading ? (
        <div className="pfcal-loading">
          <FaSpinner className="pfcal-spinner" />
          <p>Loading your calendar...</p>
        </div>
      ) : error ? (
        <div className="pfcal-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="pfcal-retry-btn">
            Try Again
          </button>
        </div>
      ) : calendarEvents.length > 0 ? (
        <>
          <div className="pfcal-day-view">
            <FullCalendar
              plugins={[dayGridPlugin, listPlugin]}
              initialView="listWeek"
              headerToolbar={false}
              height={220}
              events={calendarEvents}
              eventDisplay="block"              // NEW
              eventContent={renderCompactEventContent} // NEW
            />
          </div>
          {!isUploading && !isClearing && (
            <button 
              className="pfcal-clear-btn" 
              onClick={handleClearCalendar}
              title="Clear all calendar events"
            >
              <FaTrash className="pfcal-btn-icon" /> Clear Calendar
            </button>
          )}
        </>
      ) : (
        <div className="pfcal-upload-options">
          <button 
            className="pfcal-upload-btn pfcal-csv-btn"
            onClick={() => handleUploadClick('csv')}
          >
            <FaFileCsv className="pfcal-btn-icon" />
            <span>Upload CSV</span>
          </button>
          <button 
            className="pfcal-upload-btn pfcal-ics-btn"
            onClick={() => handleUploadClick('ics')}
          >
            <FaCalendarPlus className="pfcal-btn-icon" />
            <span>Upload ICS</span>
          </button>
        </div>
      )}

      {uploadType && (
        <div className="pfcal-upload-section">
          <div className="pfcal-upload-header">
            <h5 className="pfcal-upload-title">
              {uploadType === 'csv' ? 'Upload Timetable CSV' : 'Upload Calendar ICS'}
            </h5>
            <button 
              className="pfcal-close-upload" 
              onClick={() => setUploadType(null)}
              aria-label="Close upload"
              disabled={isUploading}
            >
              <FaTimes size={12} />
            </button>
          </div>
          
          <p className="pfcal-upload-info">
            {uploadType === 'csv' 
              ? 'Upload your timetable CSV exported from your institution\'s system.'
              : 'Upload an ICS file exported from your calendar app.'}
          </p>
          
          <div className="pfcal-file-input-wrapper">
            <input
              ref={fileInputRef}
              type="file"
              accept={uploadType === 'csv' ? '.csv' : '.ics'}
              onChange={handleFileChange}
              className="pfcal-file-input"
              id="calendar-file-input"
              disabled={isUploading}
            />
            <label htmlFor="calendar-file-input" className={`pfcal-file-label ${isUploading ? 'pfcal-uploading' : ''}`}>
              {isUploading ? (
                <>
                  <FaSpinner className="pfcal-spinner" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FaUpload className="pfcal-upload-icon" />
                  <span>{fileName || `Choose ${uploadType.toUpperCase()} file`}</span>
                </>
              )}
            </label>
          </div>

          {isUploading && (
            <div className="pfcal-progress-wrapper">
              <div className="pfcal-progress-bar-bg">
                <div className="pfcal-progress-bar-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <div className="pfcal-progress-text">{Math.min(uploadProgress, 100)}%</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const cancelGeneration = () => {
    if (abortGenRef.current) abortGenRef.current.abort();
    setGenLoading(false);
    setGenError('Generation cancelled.');
  };

  const buildSimpleTasks = useCallback(() => {
    return (domainProgress || []).map(d => ({
      domain: d.domainKey,
      title: d.nextStep ? stripStepNumber(d.nextStep) : `${d.domainKey} reflective review`,
      description: `SMART step for ${d.domainKey}. Progress ${d.progress}/${d.total}.`
    }));
  }, [domainProgress, stripStepNumber]);

  // derived 12h hour for UI slider
  const uiHour12 = useMemo(() => {
    const h = scheduleStartHour % 24;
    if (h === 0) return 12;
    if (h > 12) return h - 12;
    return h;
  }, [scheduleStartHour]);

  // when user moves slider / toggles AM-PM
  const updateHourFromUI = useCallback((uiHour, meridiem) => {
    const clamped = Math.min(Math.max(Number(uiHour) || 1, 1), 12);
    let h24 = clamped;
    if (meridiem === 'PM' && clamped !== 12) h24 = clamped + 12;
    if (meridiem === 'AM' && clamped === 12) h24 = 0;
    setScheduleStartHour(h24);
    setConfigPreset('custom');
  }, []);

  // FIX: define applyPresetTime so JSX handlers can reference it
  const applyPresetTime = useCallback(
    (preset) => {
      if (preset === 'morning') {
        setScheduleStartHour(9);
        setConfigMeridiem('AM');
        setScheduleDurationMins(60);
      } else if (preset === 'afternoon') {
        setScheduleStartHour(14); // 2 PM
        setConfigMeridiem('PM');
        setScheduleDurationMins(60);
      }
      setConfigPreset(preset);
    },
    []
  );

  const toggleAvoidDay = useCallback((dayIndex) => {
    setConfigAvoidDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  }, []);

  // NEW: central helper for "avoided" days (weekends + user-selected)
  const isAvoidedDay = useCallback(
    (dayIndex) => dayIndex === 0 || dayIndex === 6 || configAvoidDays.includes(dayIndex),
    [configAvoidDays]
  );

  // adjust scheduling helper to respect avoid-days (for expanded view tasks)
  const getNextWorkday = (date = new Date(), offsetDays = scheduleOffsetDays) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offsetDays);
    while (isAvoidedDay(d.getDay())) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  };

  // Adjusted generateICSFromTasks to use config (hour + duration + offset)
  const generateICSFromTasks = useCallback(() => {
    setGenError(null);
    setGenLoading(true);
    try {
      const tasks = buildSimpleTasks();
      if (!tasks.length) {
        setGenError('No domain tasks to export.');
        return;
      }
      const now = new Date();
      let currentDate = getNextWorkday(now, scheduleOffsetDays);
      const endWindow = new Date(currentDate);
      endWindow.setDate(endWindow.getDate() + 7);
      const eventsLines = [];
      const isoCompact = d => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

      tasks.forEach((task, idx) => {
        if (currentDate > endWindow) return;
        // Respect all avoided days (weekends + user-selected)
        while (isAvoidedDay(currentDate.getDay())) {
          currentDate.setDate(currentDate.getDate() + 1);
          if (currentDate > endWindow) return;
        }
        const start = new Date(currentDate);
        start.setHours(scheduleStartHour, 0, 0, 0);
        const end = new Date(start.getTime() + scheduleDurationMins * 60000);
        const uid = `medmap-${task.domain}-${idx}-${isoCompact(now)}`;
        eventsLines.push([
          'BEGIN:VEVENT',
          `UID:${uid}`,
          `DTSTAMP:${isoCompact(now)}`,
          `DTSTART:${isoCompact(start)}`,
          `DTEND:${isoCompact(end)}`,
          `SUMMARY:${task.title.replace(/[\r\n]/g, ' ')}`,
          `DESCRIPTION:${task.description.replace(/[\r\n]/g, ' ')}`,
          'END:VEVENT'
        ].join('\n'));
        // Move to next day after scheduling
        currentDate.setDate(currentDate.getDate() + 1);
      });

      if (!eventsLines.length) {
        setGenError('No scheduled days available in the upcoming week.');
        return;
      }

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//MedMap//Pathfinder Tasks//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        ...eventsLines,
        'END:VCALENDAR'
      ].join('\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `pathfinder_tasks_${new Date().toISOString().substring(0,10)}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      setGenError(e.message || 'Failed to generate ICS.');
    } finally {
      setGenLoading(false);
    }
  }, [
    buildSimpleTasks,
    scheduleStartHour,
    scheduleDurationMins,
    scheduleOffsetDays,
    isAvoidedDay // NEW dep to ensure latest avoid days are used
  ]);

  // Uses scheduleStartHour, scheduleDurationMins, scheduleOffsetDays (task settings)
  const syncTasksWithCalendar = useCallback(async () => {
    setGenError(null);
    setGenLoading(true);
    try {
      const tasks = buildSimpleTasks();
      if (!tasks.length) {
        setGenError('No domain tasks to sync.');
        return;
      }
      const now = new Date();
      let currentDate = getNextWorkday(now, scheduleOffsetDays);
      const eventsToSave = tasks.map((t) => {
        // Respect avoided days (weekends + user-selected)
        while (isAvoidedDay(currentDate.getDay())) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
        const start = new Date(currentDate);
        start.setHours(scheduleStartHour, 0, 0, 0);
        const end = new Date(start.getTime() + scheduleDurationMins * 60000);
        // move to next calendar day for the next task
        currentDate.setDate(currentDate.getDate() + 1);
        return {
          title: t.title,
          start,
          end,
          extendedProps: {
            domain: t.domain,
            description: t.description
          }
        };
      });
      await saveCalendarEvents(eventsToSave);
      const refreshed = await fetchCalendarEvents();
      setCalendarEvents(refreshed);
    } catch (e) {
      setGenError(e.message || 'Sync failed.');
    } finally {
      setGenLoading(false);
    }
  }, [
    buildSimpleTasks,
    scheduleStartHour,
    scheduleDurationMins,
    scheduleOffsetDays,
    isAvoidedDay // NEW dep to ensure latest avoid days are used
  ]);

  const handleGoogleCalendarSync = useCallback(async () => {
    setGoogleSyncError(null);
    setGoogleSyncSuccess(null);
    setIsGoogleSyncing(true);

    try {
      const tasks = buildSimpleTasks();
      if (!tasks.length) {
        setGoogleSyncError('No domain tasks to sync.');
        setIsGoogleSyncing(false);
        return;
      }

      const results = await syncTasksToGoogleCalendar(tasks, {
        scheduleStartHour,
        scheduleDurationMins,
        scheduleOffsetDays,
        isAvoidedDay,
      });

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (failed === 0) {
        setGoogleSyncSuccess(`Successfully synced ${successful} task${successful !== 1 ? 's' : ''} to Google Calendar!`);
      } else {
        setGoogleSyncError(`Synced ${successful} tasks, but ${failed} failed. Please try again.`);
      }

      if (failed === 0) {
        setTimeout(() => setGoogleSyncSuccess(null), 5000);
      }
    } catch (error) {
      console.error('Google Calendar sync error:', error);
      if (error.error === 'popup_closed_by_user') {
        setGoogleSyncError('Authorization cancelled. Please try again.');
      } else {
        setGoogleSyncError(`Failed to sync: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsGoogleSyncing(false);
    }
  }, [
    buildSimpleTasks,
    scheduleStartHour,
    scheduleDurationMins,
    scheduleOffsetDays,
    isAvoidedDay,
  ]);

  // Config validation helpers
  const clampInt = (val, min, max, fallback) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return fallback;
    return Math.min(Math.max(n, min), max);
  };

  const renderExpandedView = () => (
    <div className="pfcalx-expanded">
      <div className="pfcalx-headerBar">
        <div className="pfcalx-titleWrap">
          <FaCalendarAlt className="pfcalx-titleIcon pfcalx-icon-calendar" />
          <h2 className="pfcalx-title">Your Calendar</h2>
        </div>
        <div className="pfcalx-actions">
          <button
            className="pfcalx-btn pfcalx-primary"
            onClick={generateICSFromTasks}
            disabled={genLoading}
            title="Download an ICS file with scheduled task events"
          >
            {genLoading ? <FaSpinner className="pfcalx-spin" /> : 'Download ICS for External Calendar'}
          </button>
          <button
            className="pfcalx-btn pfcalx-success"
            onClick={syncTasksWithCalendar}
            disabled={genLoading}
            title="Sync tasks directly into the embedded calendar"
          >
            {genLoading ? 'Syncing…' : 'Sync Tasks to MedMap Calendar'}
          </button>
          {/* <button
            className="pfcalx-btn pfcalx-google"
            onClick={handleGoogleCalendarSync}
            disabled={isGoogleSyncing || genLoading}
            title="Sync your tasks with Google Calendar"
          >
            {isGoogleSyncing ? (
              <>
                <FaSpinner className="pfcalx-spin" />
                Syncing to Google…
              </>
            ) : (
              'Sync with Google Calendar'
            )}
          </button> */}
          <button
            className="pfcalx-btn pfcalx-danger"
            onClick={handleClearCalendar}
            disabled={isClearing || genLoading}
            title="Remove all embedded calendar events"
          >
            {isClearing ? 'Clearing…' : 'Clear'}
          </button>
          <button
            className="pfcalx-btn pfcalx-muted"
            onClick={() => setShowConfig(s => !s)}
            disabled={genLoading}
            title="Task scheduling settings"
          >
            {showConfig ? 'Hide Settings' : 'Task Preferences'}
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="pfcalx-settingsShell">
          <div className="pfcalx-settingsHeaderRow">
            <div className="pfcalx-settingsTitleBlock">
              <span className="pfcalx-settingsPill">Task Preferences</span>
              <p className="pfcalx-settingsSubtitle">
                Choose when tasks appear, how long they take, and how they’re ordered in your week.
              </p>
            </div>
          </div>

          <div className="pfcalx-settingsGrid">
            {/* Time & presets */}
            <section className="pfcalx-settingsSection pfcalx-settingsSection-time">
              <header className="pfcalx-settingsSectionHeader">
                <span className="pfcalx-settingsSectionLabel">Time of Day</span>
                <span className="pfcalx-settingsSectionHint">
                  When should tasks land in your day?
                </span>
              </header>

              <div className="pfcalx-timeRow">
                <div className="pfcalx-timeBadge">
                  <span className="pfcalx-timeMain">
                    {uiHour12.toString().padStart(2, '0')}:{'00'} {configMeridiem}
                  </span>
                  <span className="pfcalx-timeSub">
                    Local time · {scheduleDurationMins} min block
                  </span>
                </div>
              </div>

              <div className="pfcalx-timeControls">
                <div className="pfcalx-sliderWrap">
                  <input
                    type="range"
                    min="1"
                    max="12"
                    value={uiHour12}
                    onChange={e => updateHourFromUI(e.target.value, configMeridiem)}
                    className="pfcalx-timeSlider"
                  />
                  <div className="pfcalx-timeTicks">
                    <span>1</span>
                    <span>6</span>
                    <span>12</span>
                  </div>
                </div>
                <div className="pfcalx-timeAmPm">
                  <button
                    type="button"
                    className={`pfcalx-chip ${configMeridiem === 'AM' ? 'pfcalx-chip-active' : ''}`}
                    onClick={() => {
                      setConfigMeridiem('AM');
                      updateHourFromUI(uiHour12, 'AM');
                    }}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    className={`pfcalx-chip ${configMeridiem === 'PM' ? 'pfcalx-chip-active' : ''}`}
                    onClick={() => {
                      setConfigMeridiem('PM');
                      updateHourFromUI(uiHour12, 'PM');
                    }}
                  >
                    PM
                  </button>
                </div>
              </div>

              <div className="pfcalx-timePresets">
                <button
                  type="button"
                  className={`pfcalx-presetBtn ${configPreset === 'morning' ? 'pfcalx-presetBtn-active' : ''}`}
                  onClick={() => applyPresetTime('morning')}
                >
                  <span className="pfcalx-presetDot pfcalx-presetDot-morning" />
                  Morning (09:00)
                </button>
                <button
                  type="button"
                  className={`pfcalx-presetBtn ${configPreset === 'afternoon' ? 'pfcalx-presetBtn-active' : ''}`}
                  onClick={() => applyPresetTime('afternoon')}
                >
                  <span className="pfcalx-presetDot pfcalx-presetDot-afternoon" />
                  Afternoon (14:00)
                </button>
              </div>
            </section>

            {/* Duration & start offset */}
            <section className="pfcalx-settingsSection pfcalx-settingsSection-duration">
              <header className="pfcalx-settingsSectionHeader">
                <span className="pfcalx-settingsSectionLabel">Duration & Start</span>
                <span className="pfcalx-settingsSectionHint">
                  Control how long tasks last and when scheduling begins.
                </span>
              </header>

              <div className="pfcalx-fieldRow">
                <label className="pfcalx-fieldGroup">
                  <span className="pfcalx-fieldLabel">Duration per Task</span>
                  <input
                    type="range"
                    min="15"
                    max="180"
                    step="15"
                    value={scheduleDurationMins}
                    onChange={e => {
                      setScheduleDurationMins(parseInt(e.target.value, 10) || 60);
                      setConfigPreset('custom');
                    }}
                    className="pfcalx-durationSlider"
                  />
                  <div className="pfcalx-fieldMetaRow">
                    <span className="pfcalx-fieldValue">{scheduleDurationMins} minutes</span>
                    <span className="pfcalx-fieldMeta">Recommended: 45–90 minutes</span>
                  </div>
                </label>
              </div>

              <div className="pfcalx-fieldRow">
                <label className="pfcalx-fieldGroup pfcalx-fieldGroup-inline">
                  <div>
                    <span className="pfcalx-fieldLabel">Start in</span>
                    <span className="pfcalx-fieldMeta">Days from today</span>
                  </div>
                  <div className="pfcalx-inlineNumber">
                    <button
                      type="button"
                      className="pfcalx-inlineNumberBtn"
                      onClick={() => setScheduleOffsetDays(d => Math.max(0, d - 1))}
                    >
                      –
                    </button>
                    <input
                      type="number"
                      min="0"
                      max="14"
                      value={scheduleOffsetDays}
                      onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        setScheduleOffsetDays(Number.isNaN(v) ? 0 : Math.min(Math.max(v, 0), 14));
                      }}
                    />
                    <button
                      type="button"
                      className="pfcalx-inlineNumberBtn"
                      onClick={() => setScheduleOffsetDays(d => Math.min(14, d + 1))}
                    >
                      +
                    </button>
                  </div>
                </label>
              </div>
            </section>

            {/* Priority & avoid days */}
            <section className="pfcalx-settingsSection pfcalx-settingsSection-structure">
              <header className="pfcalx-settingsSectionHeader">
                <span className="pfcalx-settingsSectionLabel">Task Order & Week Structure</span>
                <span className="pfcalx-settingsSectionHint">
                  Choose which tasks you see first and which days to keep clear.
                </span>
              </header>

              <div className="pfcalx-priorityRow">
                <span className="pfcalx-fieldLabel">Task Priority</span>
                <div className="pfcalx-priorityChips">
                  <button
                    type="button"
                    className={`pfcalx-chip pfcalx-chip-wide ${configPriority === 'byDomain' ? 'pfcalx-chip-active' : ''}`}
                    onClick={() => setConfigPriority('byDomain')}
                  >
                    By domain (spread evenly)
                  </button>
                  <button
                    type="button"
                    className={`pfcalx-chip pfcalx-chip-wide ${configPriority === 'byProgress' ? 'pfcalx-chip-active' : ''}`}
                    onClick={() => setConfigPriority('byProgress')}
                  >
                    Highest progress first
                  </button>
                </div>
              </div>

              <div className="pfcalx-avoidRow">
                <span className="pfcalx-fieldLabel">Avoid Days</span>
                <div className="pfcalx-weekPills">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`pfcalx-weekPill ${configAvoidDays.includes(idx) ? 'pfcalx-weekPill-off' : ''}`}
                      onClick={() => toggleAvoidDay(idx)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span className="pfcalx-fieldMeta">
                  We’ll skip tasks on any days you turn off here.
                </span>
              </div>
            </section>
          </div>

          <div className="pfcalx-settingsSummary">
            <div className="pfcalx-settingsSummaryCopy">
              <span className="pfcalx-settingsSummaryLabel">Schedule summary</span>
              <span className="pfcalx-settingsSummaryText">
                One task per non‑avoided weekday starting in {scheduleOffsetDays} day
                {scheduleOffsetDays === 1 ? '' : 's'} at {uiHour12.toString().padStart(2, '0')}:00 {configMeridiem} ({scheduleDurationMins} min).
              </span>
            </div>
            <div className="pfcalx-saveCfgRow">
              <button
                type="button"
                className="pfcalx-saveCfgBtn"
                onClick={saveTaskConfig}
                title="Persist these task scheduling settings"
              >
                Save Configuration
              </button>
              {saveFeedback && (
                <span
                  className={`pfcalx-saveFeedback ${
                    saveFeedback === 'Saved' ? 'pfcalx-saveFeedback-ok' : 'pfcalx-saveFeedback-err'
                  }`}
                >
                  {saveFeedback === 'Saved' ? 'Saved' : 'Error'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {genError && <div className="pfcalx-alert pfcalx-alert-error">{genError}</div>}

      {googleSyncError && (
        <div className="pfcalx-alert pfcalx-alert-error">
          {googleSyncError}
        </div>
      )}

      {googleSyncSuccess && (
        <div className="pfcalx-alert pfcalx-alert-success">
          {googleSyncSuccess}
        </div>
      )}

      <div className="pfcalx-mainGrid">
        <div className="pfcalx-panel pfcalx-tasksPanel">
          <div className="pfcalx-panelHeader">
            {/* replaced emoji ✅ */}
            <FaCheckCircle className="pfcalx-panelIcon pfcalx-icon-tasks" />
            <h3 className="pfcalx-panelTitle">Tasks Overview</h3>
          </div>

            {domainProgress.length === 0 && (
              <div className="pfcalx-empty">No domain progress loaded.</div>
            )}

            {domainProgress.length > 0 && (
              <ul className="pfcalx-domainList">
                {domainProgress.map(d => (
                  <li key={d.domainKey} className="pfcalx-domainItem">
                    <div className="pfcalx-domainTop">
                      <span className="pfcalx-domainName">{d.domainKey}</span>
                      <span className={`pfcalx-badge ${d.matched ? 'pfcalx-badge-ok' : 'pfcalx-badge-warn'}`}>
                        {d.progress}/{d.total}
                      </span>
                    </div>
                    <div className="pfcalx-domainNext">
                      {d.nextStep
                        ? stripStepNumber(d.nextStep)
                        : 'Completed – stretch task next'}
                    </div>
                  </li>
                ))}
              </ul>
            )}

          {generatedTasks.length > 0 && (
            <div className="pfcalx-generatedBlock">
              <div className="pfcalx-panelHeader pfcalx-generatedHeader">
                {/* replaced emoji 📝 */}
                <FaClipboardList className="pfcalx-panelIcon pfcalx-icon-smart" />
                <h3 className="pfcalx-panelTitle">SMART Tasks</h3>
              </div>
              <ul className="pfcalx-generatedList">
                {generatedTasks.map((t, i) => (
                  <li key={i} className="pfcalx-generatedItem">
                    <div className="pfcalx-generatedLine">
                      <strong>{t.domain}:</strong> {t.smartTask}
                    </div>
                    {t.rationale && (
                      <div className="pfcalx-generatedRationale">{t.rationale}</div>
                    )}
                  </li>
                ))}
              </ul>
              {lastGeneratedAt && (
                <div className="pfcalx-generatedStamp">
                  Generated at {lastGeneratedAt.toLocaleTimeString()}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pfcalx-panel pfcalx-calendarPanel">
          <div className="pfcalx-panelHeader">
            {/* replaced emoji 🗓️ */}
            <FaCalendarAlt className="pfcalx-panelIcon pfcalx-icon-schedule" />
            <h3 className="pfcalx-panelTitle">Schedule</h3>
          </div>

          {calendarEvents.length > 0 ? (
            <FullCalendar
              plugins={[dayGridPlugin, listPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek'
              }}
              height="auto"
              events={calendarEvents}
              eventDisplay="block"              // NEW: allow multi-line block cards
              eventContent={renderEventContent} // UPDATED renderer
              className="pfcalx-calendar"
            />
          ) : (
            <div className="pfcalx-uploadWrapper">
              <div className="pfcalx-uploadCard">
                <div className="pfcalx-uploadIcon">📄</div>
                <h4 className="pfcalx-uploadTitle">Timetable CSV</h4>
                <p className="pfcalx-uploadDesc">
                  Import your institution timetable to populate events automatically.
                </p>
                <button
                  className="pfcalx-btn pfcalx-outline"
                  onClick={() => handleUploadClick('csv')}
                >
                  Upload CSV
                </button>
              </div>
              <div className="pfcalx-uploadCard">
                <div className="pfcalx-uploadIcon">📥</div>
                <h4 className="pfcalx-uploadTitle">Calendar ICS</h4>
                <p className="pfcalx-uploadDesc">
                  Import an existing calendar export (.ics) to view here.
                </p>
                <button
                  className="pfcalx-btn pfcalx-outline"
                  onClick={() => handleUploadClick('ics')}
                >
                  Upload ICS
                </button>
              </div>

              {uploadType && (
                <div className="pfcalx-fileZone">
                  <div className="pfcalx-fileHeader">
                    <span className="pfcalx-fileTitle">
                      {uploadType === 'csv' ? 'Upload Timetable CSV' : 'Upload Calendar ICS'}
                    </span>
                    <button
                      className="pfcalx-closeFile"
                      onClick={() => setUploadType(null)}
                      disabled={isUploading}
                      title="Close uploader"
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                  <p className="pfcalx-fileHint">
                    {uploadType === 'csv'
                      ? 'Choose your exported timetable CSV file.'
                      : 'Choose your exported .ics calendar file.'}
                  </p>
                  <div className="pfcalx-fileInputWrap">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={uploadType === 'csv' ? '.csv' : '.ics'}
                      onChange={handleFileChange}
                      disabled={isUploading}
                      className="pfcalx-hiddenInput"
                    />
                    <label
                      className={`pfcalx-fileLabel ${isUploading ? 'pfcalx-fileLabelUploading' : ''}`}
                      onClick={() => !isUploading && fileInputRef.current && fileInputRef.current.click()}
                    >
                      {isUploading ? (
                        <>
                          <FaSpinner className="pfcalx-spin" />
                          Uploading…
                        </>
                      ) : (
                        <>
                          <FaUpload className="pfcalx-fileIcon" />
                          {fileName || 'Choose file'}
                        </>
                      )}
                    </label>
                  </div>
                  {isUploading && (
                    <div className="pfcalx-progressBar">
                      <div
                        className="pfcalx-progressFill"
                        style={{ width: `${uploadProgress}%` }}
                      />
                      <span className="pfcalx-progressText">
                        {Math.min(uploadProgress, 100)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Load saved task scheduling config (localStorage)
  const applyStoredTaskConfig = useCallback((cfg = {}) => {
    if (typeof cfg.scheduleStartHour === 'number') setScheduleStartHour(cfg.scheduleStartHour);
    if (typeof cfg.scheduleDurationMins === 'number') setScheduleDurationMins(cfg.scheduleDurationMins);
    if (typeof cfg.scheduleOffsetDays === 'number') setScheduleOffsetDays(cfg.scheduleOffsetDays);
    if (cfg.configMeridiem === 'AM' || cfg.configMeridiem === 'PM') setConfigMeridiem(cfg.configMeridiem);
    if (typeof cfg.configPreset === 'string') setConfigPreset(cfg.configPreset);
    if (typeof cfg.configPriority === 'string') setConfigPriority(cfg.configPriority);
    if (Array.isArray(cfg.configAvoidDays)) setConfigAvoidDays(cfg.configAvoidDays);
  }, []);

  const loadTaskConfigFromStorage = useCallback(() => {
     if (typeof window === 'undefined') return;
     let raw = window.localStorage.getItem(TASK_CONFIG_STORAGE_KEY);
     if (!raw) {
       for (const legacyKey of LEGACY_TASK_CONFIG_KEYS) {
         const legacy = window.localStorage.getItem(legacyKey);
         if (legacy) { raw = legacy; break; }
       }
     }
     if (!raw) return;
     try {
       applyStoredTaskConfig(JSON.parse(raw));
     } catch (err) {
       console.warn('[CalendarBox] Failed to parse stored task config', err);
     }
  }, [applyStoredTaskConfig]);

  useEffect(() => {
    loadTaskConfigFromStorage();
  }, [loadTaskConfigFromStorage]);

  useEffect(() => () => {
    if (saveFeedbackTimerRef.current) clearTimeout(saveFeedbackTimerRef.current);
  }, []);

  const currentTaskConfig = useMemo(() => ({
    scheduleStartHour,
    scheduleDurationMins,
    scheduleOffsetDays,
    configMeridiem,
    configPreset,
    configPriority,
    configAvoidDays,
    savedAt: Date.now()
  }), [
    scheduleStartHour,
    scheduleDurationMins,
    scheduleOffsetDays,
    configMeridiem,
    configPreset,
    configPriority,
    configAvoidDays
  ]);

  const saveTaskConfig = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(TASK_CONFIG_STORAGE_KEY, JSON.stringify(currentTaskConfig));
      setSaveFeedback('Saved');
      if (saveFeedbackTimerRef.current) clearTimeout(saveFeedbackTimerRef.current);
      saveFeedbackTimerRef.current = setTimeout(() => setSaveFeedback(null), 2600);
    } catch (err) {
      console.error('[CalendarBox] Failed to save task config', err);
      setSaveFeedback('Error');
      if (saveFeedbackTimerRef.current) clearTimeout(saveFeedbackTimerRef.current);
      saveFeedbackTimerRef.current = setTimeout(() => setSaveFeedback(null), 3200);
    }
  }, [currentTaskConfig]);

  return (
    <div className={`pfcal-box info-box ${isExpandedInCenter ? 'pfcal-center-expanded pfcalx-fullBleed' : 'pfcal-standard'}`}>
      {!isExpandedInCenter && (
        isExpanded ? (
          <button className="pfcal-control-btn pfcal-close" onClick={onClose} title="Close">
            <FaTimes size={12} />
          </button>
        ) : null /* EXPAND now lives in compact top bar */
      )}
      {isExpandedInCenter ? renderExpandedView() : renderCompactView()}
      <style jsx>{`
        /* --- Compact Calendar Top Bar (isolated selectors) --- */
        .pfcalc-topbar {
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:0.75rem;
          background:#ffffff;
          border:1px solid #e2e8f0;
          padding:0.55rem 0.75rem;
          border-radius:12px;
        }
        .pfcalc-downloadBtn {
          flex:1;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:0.55rem;
          font-size:0.65rem;
          font-weight:600;
          letter-spacing:.4px;
          padding:0.55rem 0.9rem;
          border-radius:8px;
          border:1px solid #dbe2e9;
          background:linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%);
          color:#0f172a;
          cursor:pointer;
          position:relative;
          transition:background .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .pfcalc-downloadBtn:hover:not(:disabled) {
          background:linear-gradient(135deg,#f1f5f9 0%,#e2e8f0 100%);
          box-shadow:0 4px 12px rgba(0,0,0,0.05);
          transform:translateY(-2px);
        }
        .pfcalc-downloadBtn:active:not(:disabled) {
          transform:translateY(0);
          box-shadow:0 2px 6px rgba(0,0,0,0.04);
        }
        .pfcalc-downloadBtn:disabled {
          opacity:.55;
          cursor:default;
        }
        .pfcalc-downloadIcon {
          color:#2563eb;
          width:0.85rem;
          height:0.85rem;
        }
        .pfcalc-spin {
          animation:pfcalc-rot 1s linear infinite;
          width:0.85rem;
          height:0.85rem;
          color:#2563eb;
        }
        @keyframes pfcalc-rot { to { transform:rotate(360deg); } }

        .pfcalc-expandBtn {
          flex:0 0 auto;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:34px;
          height:34px;
          border-radius:8px;
          border:1px solid #dbe2e9;
          background:#ffffff;
          color:#475569;
          cursor:pointer;
          transition:background .18s ease, color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .pfcalc-expandBtn:hover {
          background:#f1f5f9;
          color:#1e293b;
          box-shadow:0 4px 10px rgba(0,0,0,0.05);
          transform:translateY(-2px);
        }
        .pfcalc-expandBtn:active {
          transform:translateY(0);
          box-shadow:0 2px 6px rgba(0,0,0,0.04);
        }

        @media (max-width:640px) {
          .pfcalc-topbar {
            flex-direction:column;
            align-items:stretch;
            padding:0.6rem 0.65rem;
            gap:0.5rem;
          }
          .pfcalc-expandBtn {
            width:100%;
            height:32px;
          }
          .pfcalc-downloadBtn {
            font-size:0.62rem;
            padding:0.5rem 0.75rem;
          }
        }

        /* @media (prefers-color-scheme: dark) {
          .pfcalc-topbar {
            background:#1e293b;
            border-color:#334155;
          }
          .pfcalc-downloadBtn {
            background:linear-gradient(135deg,#1e293b 0%,#162536 100%);
            border-color:#334155;
            color:#f1f5f9;
          }
          .pfcalc-downloadBtn:hover:not(:disabled) {
            background:linear-gradient(135deg,#27364a 0%,#1e293b 100%);
          }
          .pfcalc-expandBtn {
            background:#1e293b;
            border-color:#334155;
            color:#94a3b8;
          }
          .pfcalc-expandBtn:hover {
            background:#27364a;
            color:#f1f5f9;
          }
        } */

        /* --- Existing styles (unchanged below) --- */
        .pfcal-box.info-box.pfcal-standard {
          background:#ffffff;
          border:1px solid #e5e7eb;
          border-radius:12px;
          padding:1rem;
          box-shadow:0 1px 2px rgba(0,0,0,0.04);
          display:flex;
          flex-direction:column;
          gap:0.65rem;
          transition:box-shadow .25s ease, transform .25s ease;
        }
        .pfcal-box.info-box.pfcal-standard:hover {
          transform:translateY(-1px);
          box-shadow:0 6px 16px rgba(0,0,0,0.06);
        }
        .pfcal-standard .pfcal-title {
          margin:0;
          font-size:0.85rem;
          font-weight:600;
          letter-spacing:.5px;
          color:#0f172a;
        }
        .pfcal-standard .pfcal-loading,
        .pfcal-standard .pfcal-error {
          font-size:0.68rem;
          color:#475569;
        }
        .pfcal-standard .pfcal-day-view {
          border:1px solid #e2e8f0;
          border-radius:10px;
          overflow:hidden;
          background:#f8fafc;
        }
        .pfcal-standard :global(.fc) {
          font-size:0.65rem;
        }
        .pfcal-standard .pfcal-clear-btn {
          width:100%;
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:8px;
          font-size:0.63rem;
          font-weight:600;
          padding:0.5rem 0.75rem;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:0.4rem;
          transition:background .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .pfcal-standard .pfcal-clear-btn:hover {
          background:#f1f5f9;
          box-shadow:0 4px 10px rgba(0,0,0,0.04);
          transform:translateY(-2px);
        }
        .pfcal-standard .pfcal-upload-options {
          display:flex;
          flex-direction:column;
          gap:0.55rem;
        }
        .pfcal-standard .pfcal-upload-btn {
          width:100%;
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:8px;
          font-size:0.63rem;
          font-weight:600;
          padding:0.55rem 0.75rem;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:0.45rem;
          transition:background .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .pfcal-standard .pfcal-upload-btn:hover {
          background:#f8fafc;
          box-shadow:0 4px 10px rgba(0,0,0,0.05);
          transform:translateY(-2px);
        }
        .pfcal-standard .pfcal-upload-section {
          border:1px solid #e2e8f0;
          border-radius:10px;
          background:#ffffff;
          padding:0.7rem 0.75rem 0.8rem;
          display:flex;
          flex-direction:column;
          gap:0.55rem;
        }
        .pfcal-standard .pfcal-upload-title {
          font-size:0.7rem;
          font-weight:600;
          margin:0;
          color:#0f172a;
        }
        .pfcal-standard .pfcal-upload-info {
          font-size:0.6rem;
          color:#475569;
          margin:0;
          line-height:1.3;
        }
        .pfcal-standard .pfcal-file-label {
          font-size:0.6rem;
          font-weight:500;
        }
        .pfcal-standard .pfcal-progress-text {
          font-size:0.55rem;
        }

        /* --- Compact list view emphasis tweaks --- */
        .pfcal-compact .pfcal-day-view :global(.fc-list) {
          border-radius:10px;
          overflow:hidden;
          border:1px solid #e2e8f0;
        }
        .pfcal-compact .pfcal-day-view :global(.fc-list-event-time),
        .pfcal-compact .pfcal-day-view :global(.fc-list-day-cushion) {
          display:none !important;
        }
        .pfcalc-listEvent {
          display:flex;
          flex-direction:column;
          gap:4px;
          padding:8px 10px 9px;
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:8px;
          box-shadow:0 1px 2px rgba(0,0,0,0.03);
          font-size:0.58rem;
          line-height:1.3;
          transition:box-shadow .18s ease, transform .18s ease, border-color .18s ease;
        }
        .pfcalc-listEvent:hover {
          box-shadow:0 4px 10px rgba(0,0,0,0.06);
          transform:translateY(-2px);
          border-color:#cbd5e1;
        }
        .pfcalc-listTitle {
          font-size:0.62rem;
          font-weight:600;
          color:#0f172a;
          letter-spacing:.35px;
        }
        .pfcalc-listDesc {
          font-size:0.58rem;
          color:#475569;
          font-weight:500;
          background:#f1f5f9;
          padding:4px 6px;
          border-radius:6px;
          line-height:1.35;
        }
        .pfcalc-listMeta {
          display:flex;
          flex-wrap:wrap;
          gap:4px;
          margin-top:2px;
        }
        .pfcalc-listMetaItem {
          background:#f8fafc;
          border:1px solid #e2e8f0;
          padding:2px 6px;
          border-radius:5px;
          font-size:0.5rem;
          color:#64748b;
          letter-spacing:.3px;
        }
        /* Tighten table cell padding so description space is maximized */
        .pfcal-compact .pfcal-day-view :global(.fc-list-table td) {
          padding:4px 6px !important;
        }
        /* Dark mode */
        /* @media (prefers-color-scheme: dark) {
          .pfcalc-listEvent {
            background:#1e293b;
            border-color:#334155;
            box-shadow:0 1px 2px rgba(0,0,0,0.45);
          }
          .pfcalc-listEvent:hover {
            border-color:#475569;
            box-shadow:0 4px 12px rgba(0,0,0,0.55);
          }
          .pfcalc-listTitle { color:#f1f5f9; }
          .pfcalc-listDesc {
            background:#27364a;
            color:#cbd5e1;
          }
          .pfcalc-listMetaItem {
            background:#27364a;
            border-color:#334155;
            color:#94a3b8;
          }
        } */
        @media (max-width:640px) {
          .pfcalc-listEvent { padding:7px 8px; }
          .pfcalc-listTitle { font-size:0.6rem; }
          .pfcalc-listDesc { font-size:0.54rem; }
        }

        /* Keep expanded (center) styles below untouched */
        /* --- New Expanded Calendar UI (pfcalx- namespace) --- */
        .pfcalx-expanded {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          padding: 0.75rem 0.25rem 1.5rem;
        }
        @media (min-width: 640px) {
          .pfcalx-expanded { padding: 0.75rem 0.5rem 1.75rem; }
        }
        .pfcalx-headerBar {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
          justify-content: space-between;
          background: #f8fafc;
          padding: 0.85rem 1.1rem;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
        }
        .pfcalx-titleWrap {
          display: flex;
          align-items: center;
          gap: 0.55rem;
        }
        .pfcalx-titleIcon,
        .pfcalx-panelIcon {
          display: inline-flex;
          width: 1.15rem;
          height: 1.15rem;
        }
        .pfcalx-icon-calendar { color:#2563eb; }
        .pfcalx-icon-tasks { color:#10b981; }
        .pfcalx-icon-smart { color:#6366f1; }
        .pfcalx-icon-schedule { color:#f59e0b; }
        /* optional hover emphasis */
        .pfcalx-panelHeader:hover .pfcalx-panelIcon { filter: brightness(1.1); }

        .pfcalx-title {
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0;
          letter-spacing: .5px;
          color: #0f172a;
        }
        .pfcalx-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.55rem;
        }
        .pfcalx-btn {
          border: none;
          cursor: pointer;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: .4px;
          padding: 0.55rem 0.9rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          transition: background .18s ease, transform .18s ease;
          white-space: nowrap;
        }
        .pfcalx-btn:disabled { opacity: .55; cursor: default; }
        .pfcalx-primary { background:#2563eb; color:#fff; }
        .pfcalx-primary:hover:not(:disabled){ background:#1d4ed8; transform:translateY(-2px); }
        .pfcalx-success { background:#10b981; color:#fff; }
        .pfcalx-success:hover:not(:disabled){ background:#059669; transform:translateY(-2px); }
        .pfcalx-danger { background:#ef4444; color:#fff; }
        .pfcalx-danger:hover:not(:disabled){ background:#dc2626; transform:translateY(-2px); }
        .pfcalx-muted { background:#0d9488; color:#fff; }
        .pfcalx-muted:hover:not(:disabled){ background:#0f766e; transform:translateY(-2px); }
        .pfcalx-google { background:#4285f4; color:#fff; }
        .pfcalx-google:hover:not(:disabled){ background:#357ae8; transform:translateY(-2px); }
        .pfcalx-outline {
          background:#fff;
          color:#1e293b;
          border:1px solid #cbd5e1;
        }
        .pfcalx-outline:hover:not(:disabled){ background:#f1f5f9; }

        .pfcalx-spin { animation: pfcalx-spin 1s linear infinite; }
        @keyframes pfcalx-spin { to { transform: rotate(360deg);} }

        .pfcalx-settingsShell {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 1rem;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }
        @media (min-width: 640px) {
          .pfcalx-settingsShell { padding: 1.25rem; }
        }

        .pfcalx-settingsHeaderRow {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .pfcalx-settingsTitleBlock {
                   display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .pfcalx-settingsPill {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #0f172a;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 0.3rem 0.6rem;
        }
               .pfcalx-settingsPill::before {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          background: #2563eb;
          border-radius: 50%;
        }
        .pfcalx-settingsSubtitle {
          margin: 0;
          font-size: 0.75rem;
          color: #475569;
        }

        .pfcalx-settingsGrid {
          display: grid;
          gap: 1rem;
        }
        @media (min-width: 900px) {
          .pfcalx-settingsGrid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }

        .pfcalx-settingsSection {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 0.9rem;
        }
        .pfcalx-settingsSectionHeader {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .pfcalx-settingsSectionLabel {
          font-size: 0.85rem;
          font-weight: 600;
          color: #0f172a;
          letter-spacing: 0.2px;
        }
        .pfcalx-settingsSectionHint {
          font-size: 0.7rem;
          color: #64748b;
        }

        /* Time controls */
        .pfcalx-timeRow {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.6rem;
          background: #ffffff;
          border: 1px dashed #e2e8f0;
          border-radius: 10px;
        }
        .pfcalx-timeBadge {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .pfcalx-timeMain {
          font-size: 0.95rem;
          font-weight: 700;
          color: #0f172a;
        }
        .pfcalx-timeSub {
          font-size: 0.7rem;
          color: #64748b;
        }

        .pfcalx-timeControls {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 0.75rem;
          align-items: center;
        }
        .pfcalx-sliderWrap {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }
        .pfcalx-timeSlider {
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 999px;
          background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
          outline: none;
        }
        .pfcalx-timeSlider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #2563eb;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
          cursor: pointer;
        }
        .pfcalx-timeTicks {
          display: flex;
          justify-content: space-between;
          font-size: 0.65rem;
          color: #94a3b8;
        }
        .pfcalx-timeAmPm {
          display: inline-flex;
          gap: 0.4rem;
        }
        .pfcalx-chip {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #334155;
          font-weight: 600;
          font-size: 0.7rem;
          padding: 0.4rem 0.65rem;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, color 0.18s ease;
        }
        .pfcalx-chip:hover { background: #f1f5f9; transform: translateY(-1px); }
        .pfcalx-chip-active { background: #2563eb; color: #ffffff; border-color: #1d4ed8; }

        .pfcalx-timePresets {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
        }
        .pfcalx-presetBtn {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #334155;
          font-weight: 600;
          font-size: 0.7rem;
          padding: 0.45rem 0.7rem;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, color 0.18s ease;
        }
        .pfcalx-presetBtn:hover { background: #f8fafc; transform: translateY(-1px); }
        .pfcalx-presetBtn-active { border-color: #2563eb; color: #2563eb; background: #eef2ff; }
        .pfcalx-presetDot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .pfcalx-presetDot-morning { background: #22c55e; }
        .pfcalx-presetDot-afternoon { background: #f59e0b; }

        /* Duration & start */
        .pfcalx-fieldRow {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.5rem;
        }
        .pfcalx-fieldGroup {
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .pfcalx-fieldGroup-inline {
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        }
        .pfcalx-fieldLabel {
          font-size: 0.75rem;
          font-weight: 600;
          color: #0f172a;
        }
        .pfcalx-fieldMeta,
        .pfcalx-fieldMetaRow {
          font-size: 0.7rem;
          color: #64748b;
          display: flex;
          gap: 0.5rem;
        }
        .pfcalx-durationSlider {
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 999px;
          background: linear-gradient(90deg, #e2e8f0, #cbd5e1);
          outline: none;
        }
        .pfcalx-durationSlider::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #10b981;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.35);
          cursor: pointer;
        }
        .pfcalx-inlineNumber {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.25rem 0.35rem;
        }
        .pfcalx-inlineNumber input {
          width: 3rem;
          border: none;
          background: transparent;
          text-align: center;
          font-size: 0.75rem;
          color: #0f172a;
          outline: none;
        }
        .pfcalx-inlineNumberBtn {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #334155;
          border-radius: 8px;
          padding: 0.2rem 0.5rem;
          cursor: pointer;
          transition: background 0.18s ease, transform 0.18s ease;
        }
        .pfcalx-inlineNumberBtn:hover { background: #e2e8f0; transform: translateY(-1px); }

        /* Priority & avoid days */
        .pfcalx-priorityRow {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .pfcalx-priorityChips {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
        }
        .pfcalx-chip-wide {
          min-width: 190px;
          justify-content: center;
        }

        .pfcalx-avoidRow {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .pfcalx-weekPills {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 0.35rem;
        }
        .pfcalx-weekPill {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #334155;
          font-weight: 700;
          font-size: 0.7rem;
          border-radius: 10px;
          padding: 0.4rem 0;
          cursor: pointer;
          transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease, color 0.18s ease;
        }
        .pfcalx-weekPill:hover { background: #f8fafc; transform: translateY(-1px); }
        .pfcalx-weekPill-off {
          background: #fff7ed;
          border-color: #fdba74;
          color: #c2410c;
        }

        /* Summary */
        .pfcalx-settingsSummary {
          display:flex;
          flex-direction:column;
          gap:0.85rem;
          padding-top:0.9rem;
          border-top:1px solid #e5e7eb;
        }
        .pfcalx-settingsSummaryLabel {
          font-size: 0.75rem;
          font-weight: 700;
          color: #0f172a;
        }
        .pfcalx-settingsSummaryText {
          font-size: 0.75rem;
          color: #475569;
        }
        .pfcalx-settingsSummaryCopy {
          display:flex;
          flex-direction:column;
          gap:0.35rem;
        }
        .pfcalx-saveCfgRow {
          display:flex;
          flex-direction:column;
          gap:0.4rem;
        }
         .pfcalx-saveCfgBtn {
          width:100%;
          border:none;
          border-radius:12px;
          padding:0.95rem 1.1rem;
          background:linear-gradient(120deg,#2563eb,#7c3aed);
          color:#ffffff;
          font-size:0.95rem;
          font-weight:700;
          letter-spacing:.4px;
          text-transform:uppercase;
          box-shadow:0 14px 30px rgba(79,70,229,0.25);
          cursor:pointer;
          transition:transform .2s ease, box-shadow .2s ease, filter .2s ease;
        }
        .pfcalx-saveCfgBtn:hover {
          transform:translateY(-2px);
          box-shadow:0 18px 32px rgba(79,70,229,0.3);
          filter:brightness(1.02);
        }
        .pfcalx-saveCfgBtn:active {
          transform:translateY(0);
          box-shadow:0 8px 18px rgba(79,70,229,0.25);
        }
         .pfcalx-saveFeedback {
           font-size:0.55rem;
           font-weight:600;
           padding:0.25rem 0.55rem;
           border-radius:6px;
          align-self:flex-start;
         }
         .pfcalx-saveFeedback-ok {
           background:#ecfdf5;
           color:#047857;
           border:1px solid #d1fae5;
         }
         .pfcalx-saveFeedback-err {
           background:#fee2e2;
           color:#b91c1c;
           border:1px solid #fca5a5;
         }

        @media (max-width:640px){
          .pfcalx-saveCfgBtn { font-size:0.88rem; }
        }
        /* @media (prefers-color-scheme: dark){
          .pfcalx-saveCfgBtn {
            background:linear-gradient(120deg,#1d4ed8,#7c3aed);
            box-shadow:0 14px 26px rgba(23,37,84,0.6);
          }
          .pfcalx-saveCfgBtn:hover { box-shadow:0 20px 34px rgba(23,37,84,0.65); }
          .pfcalx-saveFeedback-ok {
            background:#064e3b;
            color:#d1fae5;
            border-color:#065f46;
          }
        } */

        /* ===== END REWRITTEN SETTINGS STYLES ===== */

        /* Existing styles continue below */
        .pfcalx-alert {
          padding:0.6rem 0.75rem;
          border-radius:8px;
          font-size:0.7rem;
          font-weight:500;
          letter-spacing:.3px;
        }
        .pfcalx-alert-error {
          background:#fef2f2;
          border:1px solid #fecaca;
          color:#b91c1c;
        }
        .pfcalx-alert-success {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #047857;
        }

        .pfcalx-mainGrid {
          display:grid;
          gap:1rem;
        }
        @media (min-width: 900px) {
          .pfcalx-mainGrid {
            grid-template-columns: 340px 1fr;
            align-items:start;
          }
        }

        .pfcalx-panel {
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding:1rem 1.05rem;
          display:flex;
          flex-direction:column;
          gap:0.9rem;
          min-height: 220px;
        }
        .pfcalx-panelHeader {
          display:flex;
          align-items:center;
          gap:0.55rem;
          margin:0;
        }
        .pfcalx-panelTitle {
          margin:0;
          font-size:0.85rem;
          font-weight:600;
          letter-spacing:.5px;
          color:#0f172a;
        }

        .pfcalx-domainList {
          list-style:none;
          margin:0;
          padding:0;
          display:flex;
          flex-direction:column;
          gap:0.6rem;
        }
        .pfcalx-domainItem {
          background:#f8fafc;
          border:1px solid #e2e8f0;
          border-radius:10px;
          padding:0.55rem 0.65rem;
          display:flex;
          flex-direction:column;
          gap:0.35rem;
        }
        .pfcalx-domainTop {
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:0.5rem;
        }
        .pfcalx-domainName {
                   font-size:0.73rem;
          font-weight:600;
          color:#0f172a;
          letter-spacing:.4px;
        }
        .pfcalx-badge {
          font-size:0.58rem;
          padding:0.2rem 0.5rem;
          border-radius:999px;
          font-weight:600;
          letter-spacing:.4px;
        }
        .pfcalx-badge-ok {
          background:#10b981;
          color:#fff;
        }
        .pfcalx-badge-warn {
          background:#f59e0b;
          color:#fff;
        }
        .pfcalx-domainNext {
          font-size:0.63rem;
          color:#475569;
          line-height:1.4;
          white-space: pre-wrap; /* Preserve newlines but wrap */
        }
        .pfcalx-empty {
          font-size:0.65rem;
          color:#94a3b8;
          text-align:center;
          padding:0.7rem 0.3rem;
          border:1px dashed #cbd5e1;
          border-radius:10px;
        }

        .pfcalx-generatedBlock {
          display:flex;
          flex-direction:column;
          gap:0.6rem;
          margin-top:0.25rem;
        }
        .pfcalx-generatedHeader { margin-top:0.35rem; }
        .pfcalx-generatedList {
          list-style:none;
          margin:0;
          padding:0;
          display:flex;
          flex-direction:column;
          gap:0.55rem;
        }
        .pfcalx-generatedItem {
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:10px;
          padding:0.55rem 0.65rem;
        }
        .pfcalx-generatedLine {
          font-size:0.68rem;
          color:#0f172a;
          line-height:1.3;
        }
        .pfcalx-generatedRationale {
          margin-top:0.3rem;
          font-size:0.58rem;
          color:#64748b;
          line-height:1.25;
        }
        .pfcalx-generatedStamp {
          font-size:0.58rem;
          color:#64748b;
          text-align:right;
          margin-top:0.25rem;
        }

        .pfcalx-calendarPanel { position:relative; }
        .pfcalx-calendar :global(.fc) {
          font-size: 0.68rem;
        }

        .pfcalx-uploadWrapper {
          display:grid;
          gap:0.85rem;
          grid-template-columns: repeat(auto-fit,minmax(180px,1fr));
        }
        .pfcalx-uploadCard {
          background:#f8fafc;
          border:1px solid #e2e8f0;
          border-radius:12px;
          padding:0.9rem 0.85rem;
          display:flex;
          flex-direction:column;
          align-items:center;
          gap:0.55rem;
          text-align:center;
          transition:box-shadow .18s ease, transform .18s ease;
        }
        .pfcalx-uploadCard:hover {
          transform:translateY(-3px);
          box-shadow:0 6px 14px -4px rgba(0,0,0,0.08);
        }
        .pfcalx-uploadIcon {
          font-size:1.5rem;
        }
        .pfcalx-uploadTitle {
          margin:0;
          font-size:0.75rem;
          font-weight:600;
          color:#0f172a;
          letter-spacing:.4px;
        }
        .pfcalx-uploadDesc {
          margin:0;
          font-size:0.6rem;
          color:#64748b;
          line-height:1.35;
        }

        .pfcalx-fileZone {
          margin-top:0.65rem;
          background:#ffffff;
          border:1px solid #e2e8f0;
          border-radius:12px;
          padding:0.75rem 0.9rem;
          display:flex;
          flex-direction:column;
          gap:0.6rem;
        }
        .pfcalx-fileHeader {
          display:flex;
          justify-content:space-between;
          align-items:center;
        }
        .pfcalx-fileTitle {
          font-size:0.7rem;
          font-weight:600;
          color:#0f172a;
          letter-spacing:.3px;
        }
        .pfcalx-closeFile {
          border:none;
          background:#f1f5f9;
          color:#64748b;
          padding:0.35rem;
          border-radius:6px;
          cursor:pointer;
          transition:background .18s ease;
        }
        .pfcalx-closeFile:hover { background:#e2e8f0; }
        .pfcalx-fileHint {
          font-size:0.6rem;
          color:#475569;
          margin:0;
        }
        .pfcalx-fileInputWrap {
          position:relative;
        }
        .pfcalx-hiddenInput {
          position:absolute;
          inset:0;
          opacity:0;
          pointer-events:none;
        }
        .pfcalx-fileLabel {
          display:flex;
          justify-content:center;
          align-items:center;
          gap:0.45rem;
          background:#f8fafc;
          border:1px dashed #cbd5e1;
          padding:0.6rem 0.9rem;
          font-size:0.63rem;
          color:#475569;
          border-radius:10px;
          cursor:pointer;
          transition:background .18s ease, border-color .18s ease;
        }
        .pfcalx-fileLabel:hover {
          background:#f1f5f9;
          border-color:#94a3b8;
        }
        .pfcalx-fileLabelUploading {
          background:#fff;
          animation:pfcalx-pulse 1.4s ease-in-out infinite;
        }
        @keyframes pfcalx-pulse {
          0%,100% { opacity:1; }
          50% { opacity:.55; }
        }
        .pfcalx-fileIcon {
          color:#64748b;
          font-size:0.75rem;
        }
        .pfcalx-progressBar {
          position:relative;
          background:#e2e8f0;
          height:7px;
          border-radius:4px;
          overflow:hidden;
          margin-top:0.4rem;
        }
        .pfcalx-progressFill {
          position:absolute;
          top:0;
          left:0;
          height:100%;
          background:linear-gradient(90deg,#2563eb,#3b82f6);
          transition:width .25s ease;
        }
        .pfcalx-progressText {
          font-size:0.55rem;
          margin-top:0.35rem;
          text-align:right;
          color:#475569;
          letter-spacing:.4px;
        }

        /* Mobile adjustments */
        @media (max-width: 768px) {
          .pfcalx-actions { width:100%; justify-content:flex-start; }
          .pfcalx-btn { flex:1 1 auto; text-align:center; }
          .pfcalx-mainGrid { gap:0.85rem; }
          .pfcalx-panel { padding:0.85rem 0.85rem; }
          
          /* Prevent task text clipping/overflow */
          .pfcalx-domainNext, 
          .pfcalx-generatedLine,
          .pfcalx-generatedRationale {
             white-space: normal;
             overflow-wrap: break-word;
             word-break: break-word;
             font-size: 0.75rem; /* slightly larger for readability on small screens? or keep compact? user asked for no clipping. */
          }
          
          /* Calendar mobile tweaks */
          .pfcalx-calendarPanel {
             overflow-x: hidden; /* Prevent horizontal scroll on body */
          }
          .pfcalx-calendar :global(.fc) {
             font-size: 0.55rem;
             max-width: 100%;
          }
          .pfcalx-calendar :global(.fc-toolbar) {
             flex-direction: column;
             gap: 0.5rem;
             align-items: center;
          }
          .pfcalx-calendar :global(.fc-toolbar-chunk) {
             display: flex;
             justify-content: center;
             width: 100%;
             gap: 0.25rem;
          }
           /* Reduce button sizes in calendar toolbar */
          .pfcalx-calendar :global(.fc-button) {
             padding: 0.15rem 0.3rem; 
             font-size: 0.65rem; 
          }
          .pfcalx-calendar :global(.fc-view-harness) {
             /* constrain height if needed, or let it flow */
          }
        }

        /* Full-bleed overrides when center expanded */
        .pfcalx-fullBleed { width:100%; }
        .pfcalx-fullBleed .pfcalx-expanded { padding-left:0; padding-right:0; }
        .pfcalx-fullBleed .pfcalx-mainGrid { margin:0; }
        .pfcalx-fullBleed .pfcalx-headerBar,
        .pfcalx-fullBleed .pfcalx-settingsCard,
        .pfcalx-fullBleed .pfcalx-panel {
          border-radius:0;
        }
        /* Optional: allow panels to touch modal edges */
        .pfcalx-fullBleed .pfcalx-headerBar,
        .pfcalx-fullBleed .pfcalx-settingsCard { margin-left:0; margin-right:0; }
        .pfcalx-fullBleed .pfcalx-panel { margin-left:0; margin-right:0; }
      `}</style>
    </div>
  );
};

export default CalendarBox;